import React from 'react';
import { PageTitle } from '../../../../components/PageHeader';
import styled from 'styled-components';
import { PrimaryButton, SecondaryButton } from '../../../../components/Button';
import { useDataModelerEditFeature } from '../../../../utils/hooks';
import PermissionElementWrapper from '../../../PermissionControls/PermissionElementWrapper';
import KubitTooltip from '../../../../components/KubitTooltip';
import { dataModelTooltipOptions } from '../../../../components/KubitTooltip/globalTooltips';
import Badge from '../../../../components/Badge';
import { useDataModelerContext } from '../../DataModelerContext';
import { dataModelerDiscardCurrentEdits } from '../../../../redux/slices/dataModeler';
import { useAppDispatch } from '../../../../redux/hooks';

const ActionsHeaderDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 16px;
  height: 32px;
  padding-bottom: 2px;
  padding-top: 2px;
  align-items: center;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  height: 32px;
`;

const DisabledDiv = styled.div`
  margin-left: 6px;
  align-self: end;
`;

const StyledBadge = styled(Badge)`
  margin-left: 6px;
`;

// This is a customized for fields version of the DictionaryHeader, exposing the Data Modeler Edit button
// and more actions when in edit mode. Shared between the regular dictionary grid page and "edit" mode.
const DataModelerPageHeader = () => {
  const dispatch = useAppDispatch();
  const {
    isEditMode,
    schemaIsDirty,
    handleSubmitChanges,
    canConfirm,
    schemaObj,
    draftEdits,
  } = useDataModelerContext();
  const isDisabled = draftEdits.disabled ?? schemaObj?.disabled;

  const dataModelerEditFeatureEnabled = useDataModelerEditFeature();
  const isPermitted = dataModelerEditFeatureEnabled && isEditMode;
  const dataModelerActions = dataModelerEditFeatureEnabled ? (
    <PermissionElementWrapper
      isPermitted={isPermitted}
      customTooltip={
        !dataModelerEditFeatureEnabled
          ? 'This feature is only available to Direct Connect Customers'
          : 'You are not permitted to edit the Data Model'
      }
    >
      <ActionsHeaderDiv>
        <KubitTooltip
          content={
            isPermitted
              ? dataModelTooltipOptions.discardChangesButton.content
              : undefined
          }
          noOpenDelay={true}
        >
          <SecondaryButton
            onClick={() => dispatch(dataModelerDiscardCurrentEdits())}
            isOutlined
            trackContext="data modeler"
            trackAction="schema - discard"
          >
            Discard
          </SecondaryButton>
        </KubitTooltip>
        <KubitTooltip
          content={
            isPermitted
              ? dataModelTooltipOptions.confirmChangesButton.content
              : undefined
          }
          placement="top-start"
          noOpenDelay={true}
        >
          <PrimaryButton
            onClick={handleSubmitChanges}
            disabled={!canConfirm || !isEditMode}
            trackContext="data modeler"
            trackAction="schema - confirm"
          >
            Confirm
          </PrimaryButton>
        </KubitTooltip>
      </ActionsHeaderDiv>
    </PermissionElementWrapper>
  ) : null;

  return (
    <HeaderRow>
      <PageTitle title={schemaObj?.name}>
        {draftEdits.isNew ? (
          <StyledBadge size="primary" color="blue">
            DRAFT
          </StyledBadge>
        ) : schemaIsDirty ? (
          <StyledBadge size="primary" color="blue">
            EDIT
          </StyledBadge>
        ) : undefined}
        {isDisabled && <DisabledDiv>(Disabled)</DisabledDiv>}
      </PageTitle>
      {schemaIsDirty && dataModelerActions}
    </HeaderRow>
  );
};

export default DataModelerPageHeader;
